import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useNavSubscription } from './useNavSubscription';
import { UserMenu } from './UserMenu';
import { InteractionStatus } from 'src/auth/firebaseMsalCompat';
import logo from '@Assets/images/logo-80.png';
import { captureFunnelEvent, trackButtonClick } from '../../services/telemetry';
import { useDebounceCallback } from 'usehooks-ts';
import { capitalize } from '../../config/pricingConfig';
import { sampleHomePath } from '../../samples/sampleReturnTo';

const NavBar = () => {
  const {
    handleLogin,
    handleLogout,
    inProgress,
    isAuthenticated,
    emailVerified,
    name,
  } = useAuth();
  const nav = useNavSubscription(isAuthenticated && emailVerified);
  const { pathname, state } = useLocation();
  const onWorkspace = pathname.startsWith('/workspace');
  const homeTo = pathname.startsWith('/sample/') ? sampleHomePath(state) : '/';
  // Project pages have their own chrome (top ProjectToolbar + score
  // toggles), so the global navbar is hidden entirely there at every
  // screen size to keep the editing surface uncluttered.
  const onProjectPage =
    pathname.startsWith('/project/') || pathname.startsWith('/pdf-project/');

  const isBusiness = nav?.userType === 'business';
  const badgeTier = isBusiness
    ? 'Business'
    : nav?.tier
      ? capitalize(nav.tier)
      : null;
  // Show the upgrade pill for everyone except Pro / Business users — only on
  // the workspace, where "My Projects" would be redundant.
  const showUpgrade =
    isAuthenticated && onWorkspace && !isBusiness && nav?.tier !== 'pro';

  const debouncedLogin = useDebounceCallback(
    async () => {
      await trackButtonClick('NavBar', 'Log In');
      captureFunnelEvent('signup_button_clicked', { source: 'NavBar' });
      await handleLogin();
    },
    250,
    { trailing: true }
  );

  const debouncedLogout = useDebounceCallback(
    async () => {
      await trackButtonClick('NavBar', 'Log Out');
      handleLogout();
    },
    250,
    { trailing: true }
  );

  // Primary CTA (workspace): solid dark pill — same role as "Upgrade Plan" before.
  const primaryPillClass = `
    inline-flex min-h-[36px] items-center rounded-full bg-gray-800 px-3 py-1.5
    text-sm font-medium text-white transition-transform duration-100
    hover:bg-gray-700
    active:scale-95
    sm:px-4
  `;

  // Secondary CTA (elsewhere): bordered outline — matches CustomerPortal
  // "Manage billing" compact variant.
  const secondaryPillClass = `
    inline-flex min-h-[36px] items-center justify-center rounded-full border
    border-solid border-gray-700 bg-transparent px-3 py-1.5 text-sm
    font-semibold text-gray-700 transition-transform duration-100
    hover:bg-yellow-100
    active:scale-95 active:bg-yellow-200
    sm:px-4
  `;

  /** Login button for signed-out users (and the startup loading state). */
  const renderLoginButton = () => {
    if (inProgress === InteractionStatus.Startup) {
      return (
        <button
          disabled
          className={`
            min-h-[44px] min-w-[44px] cursor-not-allowed rounded-full border
            border-solid border-gray-300 bg-transparent px-3 py-1 text-sm
            text-gray-400
            sm:px-5 sm:text-base
          `}
        >
          Loading...
        </button>
      );
    }

    return (
      <button
        onClick={debouncedLogin}
        className={`
          min-h-[44px] min-w-[44px] cursor-pointer rounded-full border
          border-solid border-gray-800 bg-transparent px-3 py-1 text-sm
          text-gray-800 transition-transform duration-100
          hover:bg-yellow-400 hover:text-black
          active:scale-95 active:bg-yellow-500
          sm:px-5 sm:text-base
        `}
      >
        Log In
      </button>
    );
  };

  return (
    <>
      <nav
        className={`
          bg-gray-25 border-b-2 border-gray-300 p-4 font-[Outfit]
          ${onProjectPage ? 'hidden' : ''}
        `}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Logo and Title on the Left */}
          <div className="flex min-w-0 items-end space-x-2">
            <Link
              to={homeTo}
              onClick={async () => {
                await trackButtonClick('NavBar', 'Logo Click');
              }}
            >
              <img src={logo} alt="Logo" className="h-10 md:h-10 lg:h-12" />
            </Link>
            <div
              className={`
                mb-1 hidden truncate text-xl font-medium text-gray-800
                sm:block sm:text-2xl
                md:text-3xl
                lg:text-4xl
              `}
            >
              <Link
                to={homeTo}
                onClick={async () => {
                  await trackButtonClick('NavBar', 'Home Text Click');
                }}
                className="text-gray-800 hover:text-yellow-500"
              >
                PineappleMusicLab
              </Link>
            </div>
          </div>

          {/* Navigation and Auth Buttons on the Right */}
          <div
            className={`
              flex shrink-0 items-center space-x-1 text-sm
              sm:space-x-2 sm:text-base
              lg:space-x-4 lg:text-lg
            `}
          >
            <Link
              to="/pricing"
              onClick={async () => await trackButtonClick('NavBar', 'Pricing')}
              className={`
                rounded bg-transparent px-2 py-1 text-gray-800
                transition-transform duration-100
                hover:text-yellow-500 hover:underline
                active:scale-95 active:text-yellow-600
                sm:px-5
              `}
            >
              Pricing
            </Link>

            {isAuthenticated ? (
              <div className="flex items-center gap-2 sm:gap-3">
                {showUpgrade && (
                  <Link
                    to="/pricing"
                    onClick={async () =>
                      await trackButtonClick('NavBar', 'Upgrade Plan')
                    }
                    className={primaryPillClass}
                  >
                    Upgrade Plan
                  </Link>
                )}
                {!onWorkspace && (
                  <Link
                    to="/workspace"
                    onClick={async () =>
                      await trackButtonClick('NavBar', 'My Projects')
                    }
                    className={secondaryPillClass}
                  >
                    My Projects
                  </Link>
                )}
                <UserMenu
                  name={name}
                  tier={badgeTier}
                  onLogout={debouncedLogout}
                />
              </div>
            ) : (
              renderLoginButton()
            )}
          </div>
        </div>
      </nav>

      <div className="w-full bg-gray-300"></div>
    </>
  );
};

export default NavBar;
